"use client";

import { type ReactNode, useId, useState } from "react";

/**
 * LND — THE landing page concept, one version, developed in place. The
 * working page won the four-way comparison on 2026-08-25; palette variants
 * live on the standalone viewer (/concept/landing/lnd-001?palette=…).
 *
 * Composition rules learned in review: the proven full-surface exhibits
 * (SES-001, CHV-006, CHV-007, CHV-001) render in plate mode (chrome off) so
 * the page's own band voice does the talking; product shots stay light; the
 * night ink is reserved for the drawn receipt sheet and the claret close;
 * HRO-011's chaos-to-records motion sits midway; everything reads inside a
 * 78rem rail. Copy discipline: there is NO human approval flow in the
 * product today — definitions are proposed, versioned and inspectable, and
 * nothing on this page may claim a person approves them.
 *
 * The group is NOT rendered on the board's main scroll — the gallery
 * filters it; /concept/landing is the only place the page renders.
 */
import {
  DATA_LABEL_CLS,
  DISPLAY,
  LABEL_CLS,
  type ListItem,
  ListSection,
  LogoGrid,
  MERCURY_CLIENTS,
} from "@/components/editorial";
import { EvidenceSourcesGrid } from "@/components/editorial/evidence-sources-grid";

import type { ConceptGroup } from "../spec";
import {
  ChannelWorkspaceExhibit,
  FeedExhibit,
  SessionWithViewExhibit,
} from "./channels";
import { ToneContext } from "./drawing-kit";
import { ModelRouteCard } from "./governance";
import { Drw003Spot, DRAWINGS_A } from "./drawings-a";
import { EuFlag, UkFlag, UsFlag } from "./drawing-variant-regions";
import { TwoColumnQa } from "./faq";
import {
  IngressLayer,
  InstrumentToRecords,
  LiveInstrument,
  PhaseLegend,
} from "./hero-instrument";
import { DualCtaBand } from "./cta";
import { BandRail, CtaPair, PILLAR_DIGEST } from "./pages";
import { ActivationTimeline, AuthoringChain } from "./process";
import { GovernedAnswerExhibit } from "./sessions";

const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";
const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";
const GINK_82 = "text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]";

/** DRW-003 "The receipt", re-inked through the kit's night tone (TON-002). */
const RECEIPT_SHEET = DRAWINGS_A.find((spec) => spec.id === "DRW-003");

/* ================================================================= */
/*  Page furniture                                                    */
/* ================================================================= */

function PageShell({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <div className="px-5 sm:px-8 md:px-12">
      {/* One reading column. The page lives inside a 78rem rail — the board
          sheet's proportion — so exhibits stay composed on wide viewports
          instead of stretching to the window. */}
      <div className="mx-auto w-full max-w-[78rem]">
        {label ? <p className={`${INK_58} ${LABEL_CLS} pt-10`}>{label}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Stacked band: rail header, then the exhibit at full width. */
function Band({
  index,
  head,
  lede,
  children,
  id,
}: {
  index: string;
  head: string;
  lede: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
      <BandRail index={index} head={head}>
        {lede}
      </BandRail>
      <div className="mt-7 min-w-0">{children}</div>
    </section>
  );
}

/** Band for a section that carries its own heading: kicker only. */
function ExhibitBand({
  index,
  children,
  id,
}: {
  index: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
      <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>{index}</p>
      <div className="mt-5 min-w-0">{children}</div>
    </section>
  );
}

/** The client roster, light variant with hairlines, as landing furniture. */
function LogoStrip() {
  return (
    <section className="border-b border-dotted border-[var(--rule)] py-10">
      <p className={`${INK_58} ${DATA_LABEL_CLS}`}>Mercury Labs has delivered for</p>
      <div className="mt-5">
        <LogoGrid variant="light" clients={MERCURY_CLIENTS} />
      </div>
      <p className={`mt-4 ${INK_58} text-[0.72rem] leading-[1.5]`}>
        Client work by Mercury Labs, the studio that builds MLX.
      </p>
    </section>
  );
}

/** The pillar digest copy in FEA-001's shipped ListSection shape. */
const PILLAR_SYSTEM_ITEMS: ListItem[] = PILLAR_DIGEST.map((pillar, index) => ({
  n: "0" + String(index + 1),
  title: pillar.plain,
  body: pillar.line,
}));

/** DRW-003 "The receipt" on the kit's night ground — the TON-002 re-ink. */
function ReceiptNightPlate() {
  if (!RECEIPT_SHEET) return null;
  return (
    <div>
      <ToneContext.Provider value="night">
        {/* Full sheet with margin callouts from md; the portrait spot below
            it, so phones never pan sideways. */}
        <div className="max-md:hidden">{RECEIPT_SHEET.render()}</div>
        <div className="md:hidden">
          <Drw003Spot />
        </div>
      </ToneContext.Provider>
    </div>
  );
}

/* ================================================================= */
/*  Any model, every door                                             */
/* ================================================================= */

/* ================================================================= */
/*  Hosted regions — PLT-002's chooser with the DVR flag marks        */
/* ================================================================= */

const HOSTED_REGIONS = [
  { id: "uk", name: "United Kingdom", text: "UK region", Flag: UkFlag },
  { id: "eu", name: "European Union", text: "EU region", Flag: EuFlag },
  { id: "us", name: "United States", text: "US region", Flag: UsFlag },
] as const;

type HostedRegionId = (typeof HOSTED_REGIONS)[number]["id"];

/** A ledger row in the PLT-002 idiom. */
function RegionLedgerRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="grid min-h-8 grid-cols-[minmax(9rem,30%)_minmax(0,1fr)] border-t border-dotted border-[var(--rule)]">
      <dt className={`flex items-center border-r border-dotted border-[var(--rule)] px-3 py-1.5 ${INK_58} ${DATA_LABEL_CLS}`}>
        {label}
      </dt>
      <dd
        className={`m-0 flex items-center px-3 py-1.5 text-[0.8rem] ${
          emphasis ? `font-semibold ${INK_88}` : INK_72
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * PLT-002's region chooser, upgraded with the DVR flag marks: three radio
 * cells, the active one filled with the palette dark, ledger rows following
 * the selection. The footnote keeps DVR-003's care — the flag marks server
 * geography only.
 */
function HostedRegions() {
  const [region, setRegion] = useState<HostedRegionId>("uk");
  const uid = useId();
  const labelId = `${uid}-label`;
  const groupName = `${uid}-region`;
  const selected =
    HOSTED_REGIONS.find((option) => option.id === region) ?? HOSTED_REGIONS[0];

  return (
    <div className="mx-auto w-full max-w-[760px] border border-[var(--rule)]">
      <div className="flex min-h-[2.65rem] flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem]">
        <span className={`${INK_72} ${DATA_LABEL_CLS}`}>
          Deployment — MLX server
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          UK · EU · US
        </strong>
      </div>
      <div role="radiogroup" aria-labelledby={labelId} className="px-4 py-4">
        <p id={labelId} className={`${INK_72} ${DATA_LABEL_CLS}`}>
          Choose a hosting region
        </p>
        <div className="mt-2 grid grid-cols-3 gap-px border border-[var(--rule)] bg-[var(--rule)] max-sm:grid-cols-1">
          {HOSTED_REGIONS.map((option) => {
            const active = option.id === region;
            const Flag = option.Flag;
            return (
              <label
                key={option.id}
                className={`relative flex min-h-[5.5rem] cursor-pointer flex-col items-center justify-center gap-1.5 px-3 py-3 transition-colors duration-150 ease-out has-[:focus-visible]:outline has-[:focus-visible]:outline-1 has-[:focus-visible]:-outline-offset-[3px] has-[:focus-visible]:outline-[var(--theme-dark)] motion-reduce:transition-none ${
                  active
                    ? "bg-[var(--theme-surface-dark)] text-[var(--theme-ground)]"
                    : "bg-[var(--theme-ground)] text-[var(--theme-ink)] hover:bg-[color-mix(in_srgb,var(--theme-dark)_7%,transparent)]"
                }`}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={option.id}
                  checked={active}
                  onChange={() => setRegion(option.id)}
                  className="sr-only"
                />
                <svg
                  viewBox="0 0 100 66.67"
                  className="h-8 w-[3rem]"
                  role="presentation"
                  aria-hidden="true"
                >
                  <Flag x={0} y={0} width={100} height={66.67} opacity={active ? 1 : 0.85} />
                </svg>
                <span
                  className="text-[1.05rem] leading-none font-semibold tracking-[-0.02em]"
                  style={DISPLAY}
                >
                  {option.name}
                </span>
                <span
                  className={`${DATA_LABEL_CLS} ${active ? GINK_82 : INK_58}`}
                >
                  {option.text}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <dl className="m-0">
        <RegionLedgerRow label="Server" value="Dedicated · yours alone" emphasis />
        <RegionLedgerRow label="Region" value={selected.text} emphasis />
        <RegionLedgerRow label="Connections" value="Read-only" />
      </dl>
      <p
        className={`border-t border-dotted border-[var(--rule)] px-4 py-[0.7rem] ${INK_58} text-[0.72rem] leading-[1.5]`}
      >
        One region per deployment. The flag marks server geography only.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  The working page                                                  */
/* ================================================================= */

export function WorkingPageHero({ production = false }: { production?: boolean } = {}) {
  return (
    <header
      data-twin-stage
      className="relative isolate border-b border-dotted border-[var(--rule)]"
    >
      <IngressLayer />
      <div className={`relative z-[2] pb-14 sm:pb-16 ${production ? "pt-32 sm:pt-36" : "pt-14 sm:pt-16"}`}>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-center">
          <div>
            <h1
              style={DISPLAY}
              className={`${INK_88} max-w-[12ch] text-[clamp(2.9rem,6.2vw,5rem)] leading-[0.98] font-semibold tracking-[-0.04em]`}
            >
              One business.
              <br />
              One Twin.
              <br />
              Any AI.
            </h1>
            <p className={`mt-5 max-w-[50ch] ${INK_72} text-[1.05rem] leading-[1.65]`}>
              Generic AI has never seen how your business works. MLX reads
              your systems and writes down what things mean — that record is
              the Twin. The AI you choose answers from it, nothing else — and
              the work it produces files back in as auditable records.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
              <CtaPair />
              {/* Tertiary: a plain text link, quieter than the pair. */}
              <a
                href="/changelog"
                className={`inline-flex items-center gap-1 py-2.5 text-[0.82rem] font-medium ${INK_72} underline decoration-[color-mix(in_srgb,var(--theme-ink)_35%,transparent)] underline-offset-4 hover:decoration-current`}
              >
                What&rsquo;s new
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
            <PhaseLegend className="mt-9 max-w-[38rem]" />
          </div>
          <LiveInstrument
            sizeClass="w-[min(70vw,29rem)]"
            label="Front elevation — the AI Twin, live"
          />
        </div>
        {/* The home hero's connector grid, riser and all: the systems below
            feed the instrument above, on the same sequence clock. The lg
            margin leaves the riser's 140px clear of the row above. */}
        <div className="mt-10 lg:mt-36">
          <EvidenceSourcesGrid showRiser />
        </div>
      </div>
    </header>
  );
}

export function WorkingPage({ production = false }: { production?: boolean } = {}) {
  return (
    <PageShell label={production ? undefined : "MLX · the landing page — working concept"}>
      <WorkingPageHero production={production} />
      <LogoStrip />

      <ExhibitBand index="01 · How you start" id="how-it-works">
        <ActivationTimeline chrome={false} />
      </ExhibitBand>

      <Band
        index="02 · Ask"
        head="Ask in a sentence. Read the whole working."
        lede="One question, one governed answer — the table, the totals and what they mean."
      >
        <GovernedAnswerExhibit chrome={false} />
      </Band>

      <Band
        index="03 · The receipt"
        head="Every answer shows its working."
        lede="The receipt is not a footnote — it is the architecture: which sources, which definition, which version, and when."
      >
        <ReceiptNightPlate />
      </Band>

      <Band
        index="04 · Behind the receipt"
        head="Nothing marks its own homework."
        lede={"The receipt names a definition. This is who wrote it, proved it and judged it."}
      >
        <AuthoringChain />
      </Band>

      <Band
        index="05 · In the work"
        head="Mention a View. The agent gets your records."
        lede="Type @, mention a View, and the agent works from exactly the records it shows — opened beside the conversation."
      >
        <SessionWithViewExhibit chrome={false} />
      </Band>

      <Band
        index="06 · The feed"
        head="The discussion, where the records are."
        lede="Every channel has a feed, and the Twin is a member — it posts after overnight syncs and answers when mentioned, receipt attached."
      >
        <FeedExhibit chrome={false} />
      </Band>

      <Band
        index="07 · Chaos to records"
        head="Chaos in. Records out."
        lede="What your systems scatter — and what your agents produce — the Twin files into tables you define."
      >
        <InstrumentToRecords padded={false} chrome={false} />
      </Band>

      <Band
        index="08 · Keep"
        head="You always know what you are looking at."
        lede="Every number on screen says which records it came from and how they were filtered."
      >
        <ChannelWorkspaceExhibit chrome={false} />
      </Band>

      <Band
        index="09 · Any model, every door"
        head="Any model. Any door. Same answer."
        lede="Change the model, or come through a different door — the answer cannot move, because it comes from the Twin, not the model's memory."
      >
        <ModelRouteCard chrome={false} doors />
      </Band>

      <Band
        index="10 · Where it lives"
        head="A server that is yours alone."
        lede="Every Twin runs on its own dedicated MLX server — in the region you choose, connected to your systems read-only, with an audit trail end to end. Nothing pooled, nothing shared."
      >
        <HostedRegions />
      </Band>

      {/* FEA-001's ListSection — the home's shipped section UI, carrying
          the pillar digest copy. */}
      <section id="platform" className="scroll-mt-24 border-b border-dotted border-[var(--rule)] py-12">
        <ListSection
          section="11"
          label="The system"
          numeral="05"
          title="Five parts. One Twin."
          intro="Everything above is one system — a governed copy of your business that AI can safely use. Five parts carry it."
          items={PILLAR_SYSTEM_ITEMS}
          cols={3}
          flush
        />
      </section>

      <Band
        id="faq"
        index="12 · Questions"
        head="Asked, answered."
        lede="The questions regulated teams ask first — answered plainly."
      >
        <TwoColumnQa />
      </Band>

      {/* CTA-006's dual band as the footer hero: activation and
          partnership, on the editorial night. */}
      <section className="my-12">
        <DualCtaBand />
      </section>

      {production ? null : (
      <p className={`${INK_58} pb-6 text-[0.72rem] leading-[1.5]`}>
        LND-001 — the working page. The hero runs the live WebGL twin with its
        arrival layer and synced legend over the connector grid; the governed
        answer, View-in-Session, feed and channel workspace are SES-001,
        CHV-006, CHV-007 and CHV-001 in plate mode; HRO-011 carries the
        sprawl-to-records motion; the merged model-swap-and-doors band, the night receipt drawing and the
        flag region chooser complete the proof; PRC-002's activation timeline,
        FAQ-002's two-column Q&A and CTA-006's dual band close the page. Palette variants live on
        this viewer.
      </p>
      )}
    </PageShell>
  );
}

/* ================================================================= */

export const LANDING_GROUP: ConceptGroup = {
  id: "landing-pages",
  prefix: "LND",
  title: "Landing pages",
  blurb:
    "The landing page concept, one version, developed in place: instrument hero over the connector grid, then the proven full-surface exhibits in plate mode — SES-001's governed answer, the night receipt sheet, CHV-006's View in Session, CHV-007's feed, HRO-011's chaos-to-records motion, CHV-001's channel workspace — with the model-swap proof (PIL-003), the three doors (PIL-005), the five-part digest, the flag region chooser, PRC-002's activation timeline, FAQ-002's two-column Q&A, and CTA-006's dual band as the close. Light product shots; a 78rem rail; no approval claims (the product has no human approval flow today). Not rendered on the board scroll — view it full-page at /concept/landing/lnd-001, where palette variants live.",
  specs: [
    {
      id: "LND-001",
      title: "The working page",
      note: "The landing page, one version: instrument hero with arrival layer, synced legend and connector grid; SES-001's governed answer; the night receipt sheet; CHV-006's View in the Session; CHV-007's feed; HRO-011's sprawl-to-records motion; CHV-001's channel workspace; PIL-003's model swap; PIL-005's doors; the five-part digest; the flag region chooser; PRC-002's activation timeline; FAQ-002's Q&A; CTA-006's dual close. Palette variants on the full-page viewer.",
      surface: "ground",
      width: "full",
      interaction: "prototype",
      state: "One scroll · the version",
      href: "/concept/landing/lnd-001",
      render: () => <WorkingPage />,
    },
  ],
};
